import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { catchError, map, of, switchMap, tap, withLatestFrom } from "rxjs";
import { VendorInquiryClient } from "src/api-service";
import { normalizeError } from "../../../app-store-error.types";
import { selectSelectedCompany } from "../../../user/user.selectors";
import { VendorInquiriesActions } from "./vendor-inquiries.actions";

@Injectable()
export class VendorInquiriesEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly vendorInquiryClient = inject(VendorInquiryClient);

    // ******** GENERATE VENDOR INQUIRIES ******** //
    generateVendorInquiries$ = createEffect(() =>
        this.actions$.pipe(
            ofType(VendorInquiriesActions.generateVendorInquiries),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ quoteId, versionId, vendorInquiries }, selectedCompany]) =>
                this.vendorInquiryClient.generateVendorInquiries(quoteId, versionId, selectedCompany.id, vendorInquiries).pipe(
                    map(result => VendorInquiriesActions.generateVendorInquiriesSuccess({
                        vendorInquiries: result,
                        toastKey: 'QUOTE.VENDOR_INQUIRY.TOAST.GENERATE_SUCCESS'
                    })),
                    catchError(error => of(VendorInquiriesActions.generateVendorInquiriesFailure({
                        error: normalizeError(error),
                        toastKey: 'QUOTE.VENDOR_INQUIRY.TOAST.GENERATE_ERROR'
                    }))),
                ),
            ),
        ),
    );

    // ******** SEND VENDOR INQUIRIES ******** //
    // Each file is sent with its id (set as `fileName` on the File by the inquiry components) as the
    // multipart file name, since VendorInquiry.fileIds refers to it. attachmentNames maps id -> real name.
    sendVendorInquiries$ = createEffect(() =>
        this.actions$.pipe(
            ofType(VendorInquiriesActions.sendVendorInquiries),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ quoteId, vendorInquiries, attachmentFiles }, selectedCompany]) => {
                const files = attachmentFiles.map(file => ({ file, id: (file as File & { fileName: string }).fileName }));
                const attachmentNames = Object.fromEntries(files.map(({ file, id }) => [id, file.name]));

                return this.vendorInquiryClient.sendVendorInquiries(
                    quoteId,
                    selectedCompany?.id ?? '',
                    JSON.stringify(vendorInquiries),
                    JSON.stringify(attachmentNames),
                    files.map(({ file, id }) => ({ data: file, fileName: id })),
                ).pipe(
                    map(results => VendorInquiriesActions.sendVendorInquiriesSuccess({
                        results,
                        toastKey: 'QUOTE.VENDOR_INQUIRY.TOAST.SEND_SUCCESS'
                    })),
                    catchError(error => of(VendorInquiriesActions.sendVendorInquiriesFailure({
                        error: normalizeError(error),
                        toastKey: 'QUOTE.VENDOR_INQUIRY.TOAST.SEND_ERROR'
                    }))),
                );
            }),
        ),
    );

    // ******** LOAD VENDOR INQUIRY SUMMARIES ******** //
    loadVendorInquirySummaries$ = createEffect(() =>
        this.actions$.pipe(
            ofType(VendorInquiriesActions.loadVendorInquirySummaries),
            withLatestFrom(this.store.select(selectSelectedCompany)),
            switchMap(([{ versionId }, selectedCompany]) =>
                this.vendorInquiryClient.getVendorInquiries(versionId, selectedCompany.id).pipe(
                    map(summaries => VendorInquiriesActions.loadVendorInquirySummariesSuccess({ summaries })),
                    catchError(error => of(VendorInquiriesActions.loadVendorInquirySummariesFailure({
                        error: normalizeError(error),
                        toastKey: 'QUOTE.VENDOR_INQUIRY.TOAST.LOAD_ERROR'
                    }))),
                ),
            ),
        ),
    );

    // ******** PREVIEW VENDOR INQUIRY ******** //
    // Blob URL is SAS-signed at the API boundary; just open it.
    previewVendorInquiry$ = createEffect(() =>
        this.actions$.pipe(
            ofType(VendorInquiriesActions.previewVendorInquiry),
            tap(({ blobUrl }) => window.open(blobUrl, "_blank")),
            map(() => VendorInquiriesActions.previewVendorInquirySuccess()),
        ),
    );

}
